import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Plus,
  Search,
  ChevronRight,
  X,
  User,
  ArrowLeft,
  ArrowRight,
  Check,
  UserPlus,
  Loader2,
} from 'lucide-react';
import { businessesService, countriesService } from '@services/businesses.service';
import { usersService } from '@services/users.service';
import { PermissionGate } from '@components/PermissionGate';
import { PageGuard } from '@components/PageGuard';
import { cn } from '@lib/utils';
import type { UserSummaryResponse } from '@apptypes/index';

const statusColors: Record<string, string> = {
  active: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  pending_review: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  suspended: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  deactivated: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

const BUSINESS_TYPES = [
  { value: 'standard', label: 'Standard' },
  { value: 'preferred', label: 'Preferred' },
  { value: 'branch', label: 'Branch' },
];

// ── Step indicator ────────────────────────────────────────────────────────────

function StepIndicator({ step, total }: { step: number; total: number }) {
  const labels = ['Owner', 'Details', 'Review'];
  return (
    <div className="flex items-center gap-2 mb-6">
      {Array.from({ length: total }, (_, i) => {
        const idx = i + 1;
        const done = step > idx;
        const active = step === idx;
        return (
          <div key={idx} className="flex items-center gap-2">
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all',
                done
                  ? 'bg-[var(--gradient-to)] text-white'
                  : active
                  ? 'bg-[var(--brand-forest)] text-white'
                  : 'bg-[var(--bg-base)] text-[var(--text-muted)] border border-[var(--border-subtle)]'
              )}
            >
              {done ? <Check className="w-4 h-4" strokeWidth={1.5} /> : idx}
            </div>
            <span
              className={cn(
                'text-sm font-medium',
                active ? 'text-[var(--text-primary)]' : done ? 'text-[var(--text-accent)]' : 'text-[var(--text-muted)]'
              )}
            >
              {labels[i]}
            </span>
            {idx < total && <div className="w-8 h-px bg-[var(--bg-elevated)] mx-1" />}
          </div>
        );
      })}
    </div>
  );
}

// ── Step 1: Owner selection ───────────────────────────────────────────────────

function OwnerStep({
  selectedOwner,
  onSelect,
}: {
  selectedOwner: UserSummaryResponse | null;
  onSelect: (u: UserSummaryResponse | null) => void;
}) {
  const [mode, setMode] = useState<'search' | 'create'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [newUser, setNewUser] = useState({ phone: '', firstName: '', lastName: '', email: '', password: '' });
  const queryClient = useQueryClient();

  const { data: users = [], isLoading: searchLoading } = useQuery({
    queryKey: ['users-search', searchQuery],
    queryFn: () => usersService.list({ page: 0, size: 50 }),
    enabled: mode === 'search',
  });

  const createMutation = useMutation({
    mutationFn: () => usersService.create({
      phone: newUser.phone,
      firstName: newUser.firstName || undefined,
      lastName: newUser.lastName || undefined,
      email: newUser.email || undefined,
      password: newUser.password || undefined,
    }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['users-search'] });
      onSelect(created);
      setMode('search');
    },
  });

  const filtered = searchQuery
    ? users.filter((u) =>
        `${u.firstName} ${u.lastName} ${u.phone} ${u.email ?? ''}`.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users.slice(0, 10);

  if (selectedOwner) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-[var(--state-success-bg)] border border-[var(--state-success-border)] rounded-2xl">
          <div className="w-10 h-10 bg-[var(--bg-elevated)] rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[var(--text-primary)]">
              {selectedOwner.firstName} {selectedOwner.lastName}
            </div>
            <div className="text-sm text-[var(--text-muted)]">{selectedOwner.phone}</div>
            {selectedOwner.email && (
              <div className="text-xs text-[var(--text-muted)]">{selectedOwner.email}</div>
            )}
          </div>
          <button
            onClick={() => onSelect(null)}
            className="w-9 h-9 flex items-center justify-center rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-base)] transition-all duration-200"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <p className="text-sm text-[var(--text-muted)] text-center">Owner selected. Click Next to continue.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mode tabs */}
      <div className="flex rounded-2xl border border-[var(--border-subtle)] overflow-hidden">
        <button
          onClick={() => setMode('search')}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors',
            mode === 'search' ? 'bg-[var(--brand-forest)] text-white' : 'text-[var(--text-muted)] hover:bg-[var(--bg-base)]'
          )}
        >
          <Search className="w-4 h-4" strokeWidth={1.5} />
          Find Existing User
        </button>
        <button
          onClick={() => setMode('create')}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors',
            mode === 'create' ? 'bg-[var(--brand-forest)] text-white' : 'text-[var(--text-muted)] hover:bg-[var(--bg-base)]'
          )}
        >
          <UserPlus className="w-4 h-4" strokeWidth={1.5} />
          Create New User
        </button>
      </div>

      {mode === 'search' && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, phone or email..."
              className="w-full pl-9 pr-3 rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
            />
          </div>

          <div className="max-h-64 overflow-y-auto border border-[var(--border-subtle)] rounded-2xl divide-y divide-[var(--border-subtle)]">
            {searchLoading && (
              <div className="p-4 flex items-center justify-center gap-2 text-sm text-[var(--text-muted)]">
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                Loading users...
              </div>
            )}
            {!searchLoading && filtered.length === 0 && (
              <div className="p-4 text-center text-sm text-[var(--text-muted)]">
                No users found. Try a different search or create a new user.
              </div>
            )}
            {filtered.map((u) => (
              <button
                key={u.userId}
                onClick={() => onSelect(u)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-base)] transition-colors text-left"
              >
                <div className="w-8 h-8 bg-[var(--bg-elevated)] text-[var(--text-accent)] rounded-full flex items-center justify-center text-xs font-bold">
                  {u.firstName?.[0] ?? u.phone[1]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[var(--text-primary)] truncate">
                    {u.firstName} {u.lastName}
                  </div>
                  <div className="text-xs text-[var(--text-muted)]">{u.phone}</div>
                </div>
                <span className={cn(
                  'text-xs px-2.5 py-1 rounded-full font-semibold',
                  u.status === 'active' ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]' : 'bg-[var(--bg-base)] text-[var(--text-muted)]'
                )}>
                  {u.status}
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {mode === 'create' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">First Name</label>
              <input
                type="text"
                value={newUser.firstName}
                onChange={(e) => setNewUser((p) => ({ ...p, firstName: e.target.value }))}
                placeholder="John"
                className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Last Name</label>
              <input
                type="text"
                value={newUser.lastName}
                onChange={(e) => setNewUser((p) => ({ ...p, lastName: e.target.value }))}
                placeholder="Doe"
                className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
              />
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Phone *</label>
            <input
              type="tel"
              value={newUser.phone}
              onChange={(e) => setNewUser((p) => ({ ...p, phone: e.target.value }))}
              placeholder="+254700000000"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Email</label>
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
              placeholder="owner@example.com"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Password</label>
            <input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
              placeholder="Temporary password"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
            />
          </div>

          {createMutation.error && (
            <p className="text-xs text-[var(--state-danger)]">{(createMutation.error as any)?.message ?? 'Failed to create user.'}</p>
          )}

          <button
            onClick={() => createMutation.mutate()}
            disabled={!newUser.phone.trim() || createMutation.isPending}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
          >
            {createMutation.isPending ? (
              <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Creating user...</>
            ) : (
              <><UserPlus className="w-4 h-4" strokeWidth={1.5} /> Create User &amp; Select as Owner</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Step 2: Business details ──────────────────────────────────────────────────

interface BusinessForm {
  name: string;
  phone: string;
  email: string;
  businessType: string;
  description: string;
  kraPin: string;
  registrationNumber: string;
  countryId: string;
}

function DetailsStep({
  form,
  onChange,
}: {
  form: BusinessForm;
  onChange: (f: BusinessForm) => void;
}) {
  const set = (k: keyof BusinessForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    onChange({ ...form, [k]: e.target.value });

  const { data: countries = [] } = useQuery({
    queryKey: ['countries'],
    queryFn: () => countriesService.list(),
    staleTime: Infinity,
  });

  return (
    <div className="space-y-4">
      {countries.length > 0 && (
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Country *</label>
          <select
            value={form.countryId}
            onChange={set('countryId')}
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
          >
            {countries.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.currencyCode})</option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Business Name *</label>
        <input
          type="text"
          value={form.name}
          onChange={set('name')}
          placeholder="Acme Supplies Ltd"
          className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Phone</label>
          <input
            type="tel"
            value={form.phone}
            onChange={set('phone')}
            placeholder="+254700000000"
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
          />
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={set('email')}
            placeholder="biz@example.com"
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
          />
        </div>
      </div>

      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Business Type</label>
        <select
          value={form.businessType}
          onChange={set('businessType')}
          className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
        >
          <option value="">Select type...</option>
          {BUSINESS_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Description</label>
        <textarea
          value={form.description}
          onChange={set('description')}
          rows={2}
          placeholder="Brief description of the business"
          className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200 resize-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">KRA PIN</label>
          <input
            type="text"
            value={form.kraPin}
            onChange={set('kraPin')}
            placeholder="A000000000X"
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
          />
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Reg. Number</label>
          <input
            type="text"
            value={form.registrationNumber}
            onChange={set('registrationNumber')}
            placeholder="PVT-XXXXXXXX"
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
          />
        </div>
      </div>
    </div>
  );
}

// ── Step 3: Review & create ───────────────────────────────────────────────────

function ReviewRow({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="flex justify-between py-2 border-b border-[var(--border-subtle)] last:border-0">
      <span className="text-[14px] text-[var(--text-muted)]">{label}</span>
      <span className="text-[14px] font-medium text-[var(--text-primary)]">{value}</span>
    </div>
  );
}

// ── Wizard modal ──────────────────────────────────────────────────────────────

const EMPTY_FORM: BusinessForm = {
  name: '', phone: '', email: '', businessType: '', description: '', kraPin: '', registrationNumber: '',
  countryId: 'c0000000-0000-0000-0000-00000000004b', // Kenya default
};

function CreateBusinessWizard({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [owner, setOwner] = useState<UserSummaryResponse | null>(null);
  const [form, setForm] = useState<BusinessForm>(EMPTY_FORM);

  const createMutation = useMutation({
    mutationFn: () =>
      businessesService.adminCreate({
        ownerUserId: owner!.userId,
        name: form.name,
        countryId: form.countryId || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        businessType: form.businessType || undefined,
        description: form.description || undefined,
        kraPin: form.kraPin || undefined,
        registrationNumber: form.registrationNumber || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      onClose();
    },
  });

  const canNextStep1 = owner !== null;
  const canNextStep2 = form.name.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)] backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)]">
          <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">New Business</h3>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-base)] transition-all duration-200"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <StepIndicator step={step} total={3} />

          {step === 1 && (
            <OwnerStep selectedOwner={owner} onSelect={setOwner} />
          )}

          {step === 2 && (
            <DetailsStep form={form} onChange={setForm} />
          )}

          {step === 3 && owner && (
            <div className="space-y-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2">Owner</p>
                <div className="flex items-center gap-3 p-3 bg-[var(--bg-base)] rounded-2xl">
                  <div className="w-8 h-8 bg-[var(--bg-elevated)] text-[var(--text-accent)] rounded-full flex items-center justify-center text-xs font-bold">
                    {owner.firstName?.[0] ?? owner.phone[1]}
                  </div>
                  <div>
                    <div className="text-[14px] font-semibold text-[var(--text-primary)]">{owner.firstName} {owner.lastName}</div>
                    <div className="text-[13px] text-[var(--text-muted)]">{owner.phone}</div>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2">Business Details</p>
                <div className="bg-[var(--bg-base)] rounded-2xl px-4 py-2">
                  <ReviewRow label="Name" value={form.name} />
                  <ReviewRow label="Type" value={form.businessType} />
                  <ReviewRow label="Phone" value={form.phone} />
                  <ReviewRow label="Email" value={form.email} />
                  <ReviewRow label="KRA PIN" value={form.kraPin} />
                  <ReviewRow label="Reg. Number" value={form.registrationNumber} />
                  {form.description && (
                    <div className="py-2">
                      <span className="text-[14px] text-[var(--text-muted)]">Description</span>
                      <p className="text-[14px] text-[var(--text-secondary)] mt-0.5">{form.description}</p>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-[13px] text-[var(--text-muted)] text-center">
                Business will be created with status <strong className="text-[var(--text-primary)]">Pending Review</strong>. Wallets are provisioned automatically.
              </p>

              {createMutation.error && (
                <p className="text-sm text-[var(--state-danger)] text-center">
                  {(createMutation.error as any)?.message ?? 'Failed to create business.'}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[var(--border-subtle)]">
          {step > 1 ? (
            <button
              onClick={() => setStep((s) => s - 1)}
              disabled={createMutation.isPending}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-white px-5 py-2.5 text-[14px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200 disabled:opacity-40"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              Back
            </button>
          ) : (
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-white px-5 py-2.5 text-[14px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
            >
              Cancel
            </button>
          )}

          {step < 3 ? (
            <button
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 1 ? !canNextStep1 : !canNextStep2}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              Next
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          ) : (
            <button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              {createMutation.isPending ? (
                <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Creating...</>
              ) : (
                <><Check className="w-4 h-4" strokeWidth={1.5} /> Create Business</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export const BusinessesListPage = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showWizard, setShowWizard] = useState(false);

  const { data: businesses = [], isLoading, error } = useQuery({
    queryKey: ['businesses', page, statusFilter, search],
    queryFn: () =>
      businessesService.list({ page, limit: 20, status: statusFilter || undefined, search: search || undefined }),
  });

  return (
    <PageGuard permission="businesses.read">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-1">Platform</p>
            <h2 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">Businesses</h2>
            <p className="text-[14px] text-[var(--text-secondary)] mt-1">Manage platform businesses and their branches</p>
          </div>
          <PermissionGate permission="businesses.create">
            <button
              onClick={() => setShowWizard(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Add Business
            </button>
          </PermissionGate>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Search businesses..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-11 pr-4 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-base)] py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-default)] transition-colors duration-200"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="pending_review">Pending Review</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        {/* Table */}
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
          {isLoading && (
            <div className="p-12 text-center text-[var(--text-muted)]">Loading businesses...</div>
          )}
          {error && (
            <div className="p-12 text-center text-[var(--state-danger)]">Failed to load businesses.</div>
          )}
          {businesses.length === 0 && !isLoading && !error && (
            <div className="p-12 text-center text-[var(--text-muted)]">No businesses found.</div>
          )}
          {businesses.length > 0 && (
            <>
              <table className="w-full">
                <thead className="bg-[var(--bg-base)] border-b border-[var(--border-subtle)]">
                  <tr>
                    <th className="text-left px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">Business</th>
                    <th className="text-left px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">Status</th>
                    <th className="text-left px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">Tax Type</th>
                    <th className="text-left px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">Created</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {businesses.map((biz) => (
                    <tr key={biz.id} className="hover:bg-[var(--bg-base)] transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[var(--bg-elevated)] text-[var(--text-accent)] rounded-2xl flex items-center justify-center">
                            <Building2 className="w-5 h-5" strokeWidth={1.5} />
                          </div>
                          <div>
                            <div className="font-medium text-[var(--text-primary)]">{biz.name}</div>
                            <div className="text-[13px] text-[var(--text-muted)]">{biz.businessCode}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={cn('rounded-full px-3 py-1 text-[12px] font-semibold capitalize', statusColors[biz.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]')}>
                          {biz.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[14px] text-[var(--text-secondary)]">{biz.taxType || '—'}</td>
                      <td className="px-6 py-4 text-[14px] text-[var(--text-muted)]">{new Date(biz.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4 text-right">
                        <Link to={`/businesses/${biz.id}`} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors duration-200">
                          <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {businesses.length === 20 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-[var(--border-subtle)]">
                  <span className="text-[14px] text-[var(--text-muted)]">Page {page}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-white px-5 py-2.5 text-[14px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200 disabled:opacity-40"
                    >
                      Prev
                    </button>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={businesses.length < 20}
                      className="inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-white px-5 py-2.5 text-[14px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200 disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showWizard && <CreateBusinessWizard onClose={() => setShowWizard(false)} />}
    </PageGuard>
  );
};
